import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";

type MicPermissionStatus = "not_requested" | "granted" | "denied" | "prompt";

interface MicPermissionContextType {
  /** Whether mic is pre-warmed and ready for instant listening */
  micReady: boolean;
  /** Current permission status */
  permissionStatus: MicPermissionStatus;
  /** Whether the one-time onboarding banner has been dismissed */
  bannerDismissed: boolean;
  /** Whether we're currently requesting permission */
  isRequesting: boolean;
  /** Request mic permission and pre-warm the audio path */
  requestMicPermission: () => Promise<boolean>;
  /** Dismiss the onboarding banner (user clicked "Not now") */
  dismissBanner: () => void;
}

const MicPermissionContext = createContext<MicPermissionContextType | undefined>(undefined);

const STORAGE_KEY_MIC_READY = "micPermissionGranted";
const STORAGE_KEY_BANNER_DISMISSED = "micBannerDismissed";

export function MicPermissionProvider({ children }: { children: ReactNode }) {
  const [micReady, setMicReady] = useState(() => {
    return localStorage.getItem(STORAGE_KEY_MIC_READY) === "true";
  });
  const [permissionStatus, setPermissionStatus] = useState<MicPermissionStatus>("not_requested");
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    return localStorage.getItem(STORAGE_KEY_BANNER_DISMISSED) === "true";
  });
  const [isRequesting, setIsRequesting] = useState(false);

  // Check current permission status on mount (if Permissions API available)
  useEffect(() => {
    async function checkPermission() {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
          if (result.state === "granted") {
            setPermissionStatus("granted");
            setMicReady(true);
            localStorage.setItem(STORAGE_KEY_MIC_READY, "true");
          } else if (result.state === "denied") {
            setPermissionStatus("denied");
            setMicReady(false);
            localStorage.setItem(STORAGE_KEY_MIC_READY, "false");
          } else {
            setPermissionStatus("prompt");
          }

          // Listen for changes
          result.addEventListener("change", () => {
            if (result.state === "granted") {
              setPermissionStatus("granted");
              setMicReady(true);
              localStorage.setItem(STORAGE_KEY_MIC_READY, "true");
            } else if (result.state === "denied") {
              setPermissionStatus("denied");
              setMicReady(false);
              localStorage.setItem(STORAGE_KEY_MIC_READY, "false");
            }
          });
        } else if (localStorage.getItem(STORAGE_KEY_MIC_READY) === "true") {
          setPermissionStatus("granted");
        }
      } catch {
        // Permissions API not supported for microphone (e.g., Safari)
        if (localStorage.getItem(STORAGE_KEY_MIC_READY) === "true") {
          setPermissionStatus("granted");
        }
      }
    }

    checkPermission();
  }, []);

  const requestMicPermission = useCallback(async (): Promise<boolean> => {
    setIsRequesting(true);
    try {
      // getUserMedia must be called directly within a user gesture handler
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Pre-warm: stop tracks immediately after permission is granted
      stream.getTracks().forEach((track) => track.stop());

      setMicReady(true);
      setPermissionStatus("granted");
      localStorage.setItem(STORAGE_KEY_MIC_READY, "true");
      localStorage.setItem(STORAGE_KEY_BANNER_DISMISSED, "true");
      setBannerDismissed(true);

      console.log("[MicPermission] Microphone pre-warmed successfully");
      return true;
    } catch (error) {
      if ((error as Error).name === "NotAllowedError") {
        console.log("[MicPermission] Microphone access denied");
        setPermissionStatus("denied");
        setMicReady(false);
        localStorage.setItem(STORAGE_KEY_MIC_READY, "false");
        localStorage.setItem(STORAGE_KEY_BANNER_DISMISSED, "true");
        setBannerDismissed(true);
      } else {
        console.error("[MicPermission] Error accessing microphone:", error);
      }
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    localStorage.setItem(STORAGE_KEY_BANNER_DISMISSED, "true");
  }, []);

  return (
    <MicPermissionContext.Provider
      value={{
        micReady,
        permissionStatus,
        bannerDismissed,
        isRequesting,
        requestMicPermission,
        dismissBanner,
      }}
    >
      {children}
    </MicPermissionContext.Provider>
  );
}

export function useMicPermission() {
  const context = useContext(MicPermissionContext);
  if (!context) {
    throw new Error("useMicPermission must be used within a MicPermissionProvider");
  }
  return context;
}
